//@flow
import apiconfig from "../../../apiconfig";
import { addJRR } from "./actions";
import { addObject, addChildren } from "../../data/actions.js";
import { addSchema } from "../schema/actions.js";
import { schemaCheck } from "../schema";
import store from "../../../store";
import readPath from "../../tiny-functions/readPath";
import getOname from "../../tiny-functions/getOname";
import { digestHTTPrespondeCode } from "../../../common/functions/connectivity";
import { ApiError, ApiUnreachable, InvalidToken } from "../error";
import { toast } from "react-toastify";

const DEFAULT_API_ERROR_MESSAGE =
  "Something went wrong. Please try again later.";

function collectApiMessages(value, messages, includeObjectValues = false) {
  if (value == null) {
    return;
  }

  if (typeof value === "string") {
    const message = value.trim();
    if (message !== "") {
      messages.push(message);
    }
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) =>
      collectApiMessages(item, messages, includeObjectValues)
    );
    return;
  }

  if (typeof value === "object") {
    const messageCount = messages.length;
    [
      "message",
      "errorMessage",
      "msg",
      "detail",
      "description",
      "text",
      "reason",
      "error_description",
    ].forEach((property) =>
      collectApiMessages(value[property], messages, includeObjectValues)
    );

    if (includeObjectValues && messages.length === messageCount) {
      Object.keys(value).forEach((property) =>
        collectApiMessages(value[property], messages, includeObjectValues)
      );
    }
  }
}

export function getApiErrorMessage(
  response,
  fallback = DEFAULT_API_ERROR_MESSAGE
) {
  const messages = [];
  collectApiMessages(response?.message, messages);
  collectApiMessages(response?.messages, messages, true);
  collectApiMessages(response?.errors, messages, true);
  collectApiMessages(response?.errorMessage, messages);

  return messages.length > 0 ? [...new Set(messages)].join(" ") : fallback;
}

export function requireApiSuccess(
  response,
  fallback = DEFAULT_API_ERROR_MESSAGE
) {
  const errorNumber =
    response?.error == null || response.error === ""
      ? 0
      : Number(response.error);
  if (
    !response ||
    response.success === false ||
    (response.error != null && errorNumber !== 0)
  ) {
    throw new ApiError(getApiErrorMessage(response, fallback));
  }

  return response;
}

export async function fetchApi(
  endPoint,
  method = "get",
  payload = null,
  headers = {},
  jrr = null
) {
  const storeState = store.getState();

  jrr = jrr != null ? jrr : storeState.services.api.JRR;

  if (jrr == null || jrr === "") {
    return null;
  }
  //URL built from config file and specified end point (target)
  let url = `${apiconfig.API.ADDRESS}${endPoint}`;

  //myInit is the second fetch() argument used to specify methods, headers, body...
  let myInit = {
    method: method.toLowerCase(),
    headers: {
      "x-omicron": jrr,
      ...headers,
    },
  };

  //Body atribute is created only if the method is post. Otherwise it will pop an error
  if (method.toLowerCase() === "post") {
    myInit.body = payload;
  }

  try {
    var res = await fetch(url, myInit);
  } catch (e) {
    throw new ApiUnreachable(e.message);
  }
  return res;
}

export async function get(endPoint, options = {}) {
  const oName = getOname(endPoint);
  //This will check if a schema is avaiable on Redux Store. If yes, send version #, if not, send 0
  let state = store.getState();
  let additionalHttpGetParameters = [];

  for (const property in options.httpGetParameters || {}) {
    additionalHttpGetParameters.push(
      property + "=" + options.httpGetParameters[property]
    );
  }

  if (additionalHttpGetParameters.length > 0) {
    additionalHttpGetParameters = "&" + additionalHttpGetParameters.join("&");
  } else {
    additionalHttpGetParameters = "";
  }

  let schemaVersion =
    options.schema || schemaCheck(state.services.schema, oName);
  let completePath = `?target=${endPoint}&schema=${schemaVersion}${additionalHttpGetParameters}`;

  const res = await fetchApi(completePath, "get", null, {
    Accept: "application/json",
  });

  if (res && (await digestHTTPrespondeCode(res, state.services.api.tcount))) {
    const resData = await res.json();

    if ("schema" in resData) {
      store.dispatch(addSchema(resData, endPoint));
    }

    return resData;
  }
}

export const post = async function (endPoint, data = {}) {
  const oName = getOname(endPoint);
  //This will check if a schema is avaiable on Redux Store. If yes, send version #, if not, send 0
  let state = store.getState();
  let schemaVersion = schemaCheck(state.services.schema, oName);
  const response = await fetchApi(
    `?target=${endPoint}&schema=${schemaVersion}`,
    "post",
    JSON.stringify(data),
    {
      Accept: "application/json",
      "Content-Type": "application/json",
    }
  );

  if (await digestHTTPrespondeCode(response)) {
    const resData = await response.json();
    if ("JRR" in resData) {
      store.dispatch(addJRR(resData.JRR));
    }

    if ("schema" in resData) {
      store.dispatch(addSchema(resData, endPoint));
    }

    return resData;
  }
};

export const put = async function (endPoint, data, headers = {}) {
  const oName = getOname(endPoint);
  //This will check if a schema is avaiable on Redux Store. If yes, send version #, if not, send 0
  let state = store.getState();
  let schemaVersion = schemaCheck(state.services.schema, oName);

  const response = await fetchApi(
    `?target=${endPoint}&schema=${schemaVersion}&docpost=t`,
    "post",
    data,
    {
      Accept: "application/json",
      ...headers,
    }
  );

  if (await digestHTTPrespondeCode(response)) {
    const resData = await response.json();
    if ("JRR" in resData) {
      store.dispatch(addJRR(resData.JRR));
    }

    if ("schema" in resData) {
      store.dispatch(addSchema(resData, endPoint));
    }

    return resData;
  }
};

export const del = async function (endPoint) {
  const oName = getOname(endPoint);
  //This will check if a schema is avaiable on Redux Store. If yes, send version #, if not, send 0
  let state = store.getState();
  let schemaVersion = schemaCheck(state.services.schema, oName);
  const res = await fetchApi(
    `?target=${endPoint}&schema=${schemaVersion}`,
    "delete"
  );

  if (await digestHTTPrespondeCode(res)) {
    const resData = await res.json();
    if ("JRR" in resData) {
      store.dispatch(addJRR(resData.JRR));
    }

    return resData;
  }
};

export const load = async function (endpoint, oName = "") {
  if (oName === "") {
    oName = getOname(endpoint);
  }
  //Api Call
  let response = await get(endpoint);

  if (response && "schema" in response) {
    store.dispatch(addSchema(response, endpoint));
  }

  let { parentName, parentId } = readPath(endpoint);

  if (response && "zeroRows" in response) {
    //If object has a parent, put the object's ID on parent properties
    if (parentName != null && parentId != null) {
      store.dispatch(addChildren(parentName, parentId, oName, []));
    }
  } else if (response && "object" in response) {
    store.dispatch(
      addObject(response.name, response.object, response.object[response.pk])
    );

    /*
    //If object has a parent, put the object's ID on parent properties
    if (parentName != null && parentId != null) {
      if (response.object == null)
        throw new Error(
          "Null object came from API. Object name: " + response.name
        );
      store.dispatch(
        addProperty(parentName, parentId, oName, [response.object[response.pk]])
      );7
    }
    */
  } else if (response && "list" in response) {
    let ids = [];

    for (let object of response.list) {
      ids.push(object[response.pk]);
      store.dispatch(addObject(response.name, object, object[response.pk]));
    }

    let { parentName, parentId } = readPath(endpoint);

    //Put the list's ID on parent properties
    if (parentName != null && parentId != null) {
      store.dispatch(addChildren(parentName, parentId, oName, ids.slice()));
    }
  }
};

export const postNload = async function (
  endpoint,
  payload,
  schema,
  ...loadEndpoints
) {
  try {
    var postres = await post(endpoint, payload, schema);
    for (let loadEndpoint of loadEndpoints) {
      if (Array.isArray(loadEndpoint)) {
        await load(loadEndpoint[0], loadEndpoint[1]);
      } else {
        await load(loadEndpoint);
      }
    }
  } catch (e) {
    if (e instanceof InvalidToken) {
      return false;
    } else {
      toast.error("Problem");
      throw e;
    }
  }
  if (postres?.error) {
    toast.error(postres?.message);
  } else {
    toast.success(postres?.message);
  }
  return postres;
};

export const delNload = async function (endpoint, ...loadEndpoints) {
  try {
    await del(endpoint);
    for (let loadEndpoint of loadEndpoints) {
      if (Array.isArray(loadEndpoint)) {
        await load(loadEndpoint[0], loadEndpoint[1]);
      } else {
        await load(loadEndpoint);
      }
    }
  } catch (e) {
    if (e instanceof InvalidToken) {
      return false;
    } else {
      throw e;
    }
  }
  return true;
};
